export const LOOK_AHEAD = 3;

/**
 * Difficulty is felt in the fingers, not just in note count:
 * timing windows tighten, the double's second tap must come faster,
 * and scrubs demand more real rubbing on hard.
 * Normal wears the old hard's gloves (slightly softened); hard is merciless.
 */
const TIMING = {
  easy: { perfect: 0.11, good: 0.21, doubleWindow: 0.36, scrubRate: 3.5 },
  normal: { perfect: 0.08, good: 0.15, doubleWindow: 0.27, scrubRate: 6 },
  hard: { perfect: 0.058, good: 0.11, doubleWindow: 0.2, scrubRate: 8.5 }
};

/** Chronological taps, holds, doubles and scrubs on a single trajectory. */
export default class NoteSystem {
  constructor() {
    this.notes = [];
    this.activeHold = null; // hold OR scrub currently held
    this.timing = TIMING.normal;
  }

  load(chartNotes, difficulty = 'normal') {
    this.activeHold = null;
    this.timing = TIMING[difficulty] || TIMING.normal;
    this.notes = chartNotes
      .filter((note) =>
        Number.isFinite(note?.t) &&
        ['tap', 'hold', 'double', 'scrub'].includes(note.type) &&
        (!['hold', 'scrub'].includes(note.type) || (Number.isFinite(note.duration) && note.duration > 0))
      )
      .map((note, id) => ({
        id,
        t: note.t,
        type: note.type,
        duration: ['hold', 'scrub'].includes(note.type) ? note.duration : 0,
        gold: note.type === 'tap' && note.gold === true,
        state: 'pending',
        judgment: null,
        pressJudgment: null,
        halfAt: 0,
        agitation: 0,
        agitationTarget: note.type === 'scrub' ? note.duration * this.timing.scrubRate : 0
      }));
  }

  getVisible(songTime) {
    return this.notes
      .filter((note) => {
        if (['holding', 'scrubbing', 'half'].includes(note.state)) return true;
        if (note.state !== 'pending') return false;
        return note.t - songTime <= LOOK_AHEAD && songTime - note.t <= this.timing.good;
      })
      .sort((a, b) => a.t - b.t)
      .map((note, index) => ({
        note,
        dt: note.t - songTime,
        endTime: note.t + note.duration,
        isNext: index === 0,
        holdProgress: ['holding', 'scrubbing'].includes(note.state)
          ? Math.max(0, Math.min(1, (songTime - note.t) / note.duration))
          : 0
      }));
  }

  tryPress(songTime) {
    if (this.activeHold) return null;

    // A half-pressed double takes priority: the second tap completes it.
    const half = this.notes.find((note) => note.state === 'half');
    if (half) {
      if (songTime - half.halfAt <= this.timing.doubleWindow) {
        half.state = 'hit';
        half.judgment = half.pressJudgment;
        return { kind: 'doubleComplete', judgment: half.judgment, note: half, delta: 0 };
      }
      // Window elapsed but not yet resolved by processMisses: fall through.
    }

    let best = null;
    for (const note of this.notes) {
      if (note.state !== 'pending') continue;
      const delta = songTime - note.t;
      if (Math.abs(delta) > this.timing.good) continue;
      if (!best || Math.abs(delta) < Math.abs(best.delta)) best = { note, delta };
    }
    if (!best) return null;

    const judgment = Math.abs(best.delta) <= this.timing.perfect ? 'perfect' : 'good';
    best.note.pressJudgment = judgment;

    if (best.note.type === 'hold') {
      best.note.state = 'holding';
      this.activeHold = best.note;
      return { kind: 'holdStart', judgment, note: best.note, delta: best.delta };
    }

    if (best.note.type === 'scrub') {
      best.note.state = 'scrubbing';
      this.activeHold = best.note;
      return { kind: 'scrubStart', judgment, note: best.note, delta: best.delta };
    }

    if (best.note.type === 'double') {
      best.note.state = 'half';
      best.note.halfAt = songTime;
      return { kind: 'doubleHalf', judgment, note: best.note, delta: best.delta };
    }

    best.note.state = 'hit';
    best.note.judgment = judgment;
    return { kind: 'tap', judgment, note: best.note, delta: best.delta };
  }

  tryRelease(songTime) {
    const note = this.activeHold;
    if (!note) return null;
    this.activeHold = null;

    if (note.type === 'scrub') return this._resolveScrub(note, songTime);

    const target = note.t + note.duration;
    const delta = songTime - target;
    const absDelta = Math.abs(delta);

    if (absDelta <= this.timing.good) {
      const releaseJudgment = absDelta <= this.timing.perfect ? 'perfect' : 'good';
      const judgment =
        note.pressJudgment === 'perfect' && releaseJudgment === 'perfect'
          ? 'perfect'
          : 'good';
      note.state = 'hit';
      note.judgment = judgment;
      return { kind: 'holdComplete', judgment, note, delta };
    }

    note.state = 'missed';
    note.judgment = 'miss';
    return { kind: 'holdFailed', judgment: 'miss', note, delta };
  }

  /** Scrubs care about agitation, not release precision. Early let-go fails. */
  _resolveScrub(note, songTime) {
    const target = note.t + note.duration;
    if (songTime < target - this.timing.good) {
      note.state = 'missed';
      note.judgment = 'miss';
      return { kind: 'scrubFailed', judgment: 'miss', note, delta: songTime - target };
    }
    const agitated = note.agitation >= note.agitationTarget;
    const judgment = agitated && note.pressJudgment === 'perfect' ? 'perfect' : 'good';
    note.state = 'hit';
    note.judgment = judgment;
    return { kind: 'scrubComplete', judgment, note, delta: songTime - target };
  }

  /** Pointer wiggle / key mashing feeds the active scrub. */
  addAgitation(units) {
    const note = this.activeHold;
    if (!note || note.type !== 'scrub' || units <= 0) return 0;
    note.agitation += units;
    return units;
  }

  processMisses(songTime) {
    const missed = [];
    const resolved = [];
    for (const note of this.notes) {
      if (note.state === 'pending' && songTime - note.t > this.timing.good) {
        note.state = 'missed';
        note.judgment = 'miss';
        missed.push(note);
      } else if (note.state === 'half' && songTime - note.halfAt > this.timing.doubleWindow) {
        // Second tap never came: resolved as a generous single good, no miss.
        note.state = 'hit';
        note.judgment = 'good';
        resolved.push({ kind: 'doubleSingle', judgment: 'good', note, delta: 0 });
      }
    }

    let failedHold = null;
    if (this.activeHold) {
      const note = this.activeHold;
      const end = note.t + note.duration;
      if (note.type === 'scrub' && songTime >= end) {
        // Scrub rode to the end: auto-complete, no release timing needed.
        this.activeHold = null;
        resolved.push(this._resolveScrub(note, songTime));
      } else if (note.type === 'hold' && songTime - end > this.timing.good) {
        failedHold = note;
        failedHold.state = 'missed';
        failedHold.judgment = 'miss';
        this.activeHold = null;
      }
    }
    return { missed, failedHold, resolved };
  }

  getActiveHold() {
    return this.activeHold;
  }

  allResolved() {
    return this.notes.every((note) => !['pending', 'holding', 'scrubbing', 'half'].includes(note.state));
  }

  total() {
    return this.notes.length;
  }
}
